import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Megaphone, Plus, Search } from "lucide-react";
import { http, toastApiError } from "../lib/api";
import { toast } from "../lib/notify";
import { useAuth } from "../context/AuthContext";
import { useNavDraft } from "../lib/navDraft";
import { foldIncludes } from "../lib/trSearch";
import { parseTL } from "../components/AmountInput";
import { EMPTY_PROSPECT, toProspectForm, toProspectApi, uploadPendingSpecs } from "../components/sales/salesConst";
import ProspectForm from "../components/sales/ProspectForm";
import ProspectCard from "../components/sales/ProspectCard";
import ProspectTable from "../components/sales/ProspectTable";
import ProspectProjectForm from "../components/sales/ProspectProjectForm";

// Operasyon → Satış Pazarlama: müşteri adayları (Excel düzenindeki kart), Proje Kartı Oluştur (yetkili), 4 teklif kutusu (içerik bekleniyor)
export default function SatisPazarlama() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canCreateProject = isAdmin || !!user?.can_create_project;
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);           // açık kart id
  const [expanded, setExpanded] = useState(true);   // kart gövdesi açık mı (başlığa tıkla / ESC; kaydet sonrası kapalı)
  const [editing, setEditing] = useState(null);     // null | "new" | id
  const [form, setForm] = useState(EMPTY_PROSPECT);
  useNavDraft("satis-pazarlama", () => (editing ? { editing, form, open } : null), (d) => { setEditing(d.editing); setForm(d.form); if (d.open) setOpen(d.open); });
  const [busy, setBusy] = useState(false);
  const [projFor, setProjFor] = useState(null);     // Proje Kartı Oluştur formu açılan aday
  const [params, setParams] = useSearchParams();
  const addBtnRef = useRef(null);
  const load = useCallback(async () => { try { const { data } = await http.get("/prospects"); setItems(data); return data; } catch (e) { toastApiError(e); return []; } }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = params.get("kayit"); if (id && items.some((p) => p.id === id)) { setOpen(id); setExpanded(true); setParams((s) => { s.delete("kayit"); return s; }, { replace: true }); } }, [items, params, setParams]);
  const visible = useMemo(() => items.filter((p) => !q || foldIncludes(`${p.title} ${p.work_name} ${p.tax_office} ${p.tax_number} ${p.phone}`, q)), [items, q]);
  const current = items.find((p) => p.id === open) || null;
  const closeForm = useCallback(() => { setEditing(null); setForm(EMPTY_PROSPECT); setTimeout(() => addBtnRef.current?.focus(), 60); }, []);
  useEffect(() => {
    if (!editing) return;
    const onKey = (e) => { if (e.key !== "Escape" || document.querySelector("[data-esc-trap]")) return; e.preventDefault(); e.stopPropagation(); closeForm(); };
    window.addEventListener("keydown", onKey, true);
    setTimeout(() => document.querySelector('[data-testid="prospect-field-title"]')?.focus(), 80);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [editing, closeForm]);
  useEffect(() => {
    if (!open || editing || projFor || !expanded) return;
    const onKey = (e) => { if (e.key !== "Escape" || document.querySelector("[data-esc-trap]")) return; e.preventDefault(); e.stopPropagation(); setExpanded(false); setTimeout(() => document.querySelector('[data-testid="prospect-card-toggle"]')?.focus(), 60); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, editing, projFor, expanded]);
  const startNew = () => { setProjFor(null); setForm(EMPTY_PROSPECT); setEditing("new"); };
  const startEdit = (p) => { setProjFor(null); setForm(toProspectForm(p)); setEditing(p.id); };
  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Ünvan girin");
    setBusy(true);
    try {
      const body = toProspectApi(form, parseTL);
      const pending = form.pending_specs || [];
      const { data } = editing === "new" ? await http.post("/prospects", body) : await http.put(`/prospects/${editing}`, body);
      toast.success(editing === "new" ? "Müşteri adayı kaydedildi" : "Müşteri adayı güncellendi");
      if (pending.length) { const ok = await uploadPendingSpecs(data.id, pending, http, toastApiError); if (ok) toast.success(`${ok} şartname yüklendi`); }
      closeForm(); await load(); setOpen(data.id); setExpanded(false);
    } catch (err) { toastApiError(err); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`"${p.title}" silinsin mi?`)) return;
    try { await http.delete(`/prospects/${p.id}`); toast.success("Silindi"); setOpen(null); await load(); } catch (err) { toastApiError(err); }
  };
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="sales-page">
      <div className="flex items-start gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2"><Megaphone className="w-6 h-6 text-amber-700" /> Satış Pazarlama</h1>
          <p className="text-sm text-slate-500 mt-1">Müşteri adayları: ünvan, vergi, keşif, tutar ve değerlendirme bilgileri · olumlu adaylar için <b>Proje Kartı Oluştur</b> (yalnız Yönetici / yetkili) · geçici teminat mektubunda bu müşteriler seçilebilir.</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} data-testid="prospect-search" placeholder="Ünvan, iş, vergi no, telefon…" className="pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-md w-64 focus:border-amber-500 outline-none" /></div>
          <button ref={addBtnRef} type="button" onClick={startNew} data-testid="prospect-add" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400"><Plus className="w-4 h-4" /> Yeni Müşteri Adayı</button>
        </div>
      </div>
      {editing && <ProspectForm form={form} setForm={setForm} editing={editing} onSave={save} onCancel={closeForm} busy={busy} />}
      {projFor && <ProspectProjectForm p={projFor} onCancel={() => setProjFor(null)} onDone={async () => { setProjFor(null); await load(); }} />}
      {current && !editing && !projFor && <ProspectCard p={current} expanded={expanded} onToggle={() => setExpanded((v) => !v)} canEdit isAdmin={isAdmin} canCreateProject={canCreateProject} onEdit={() => startEdit(current)} onDelete={() => remove(current)} onCreateProject={() => setProjFor(current)} />}
      <ProspectTable items={visible} openId={open} onOpen={(p) => { setOpen(open === p.id ? null : p.id); setExpanded(true); setProjFor(null); }} />
    </div>
  );
}
